export interface TrialRequest {
  organization_id: string;
  organization_name: string;
  organization_slug: string;
  user_id: string;
  user_email: string;
  user_first_name: string | null;
  user_last_name: string | null;
  company_size: string;
  primary_use_case: string | null;
  use_case_description: string | null;
  current_solution: string | null;
  requested_max_users: number;
  estimated_monthly_conversations: string | null;
  interested_modules: string[] | null;
  estimated_monthly_visitors: string | null;
  site_description: string | null;
  how_did_you_hear: string;
  referral_source: string | null;
  submitted_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  status: string;
  subscription_plan: string;
  max_users: number;
  /** Retired 0702 (28 Sep): legacy counters, frozen as history — never shown. */
  conversation_credits: number;
  conversation_credits_used: number;
  nav_credits: number;
  nav_credits_used: number;
  /** 28 Sep (mig 0702): the org's one balance, when the list carries it. */
  total_credits_balance?: number;
  billing_state?: string | null;
  current_users: number;
  created_at: string;
}

export interface OrgModule {
  module_name: string;
  is_enabled: boolean;
  config: Record<string, unknown>;
}

export interface OrgLeadInfo {
  company_size: string | null;
  primary_use_case: string | null;
  use_case_description: string | null;
  current_solution: string | null;
  estimated_monthly_conversations: string | null;
  estimated_monthly_visitors: string | null;
  site_description: string | null;
  how_did_you_hear: string | null;
  referral_source: string | null;
  status: string | null;
  created_at: string | null;
}


export interface WorkflowListItem {
  id: number;
  name: string;
  description: string | null;
  organization_id: string;
  organization_name: string;
  states_count: number;
  created_at: string;
}

export interface MigrationSummary {
  workflow_id: number;
  workflow_name: string;
  states_created: number;
  transitions_created: number;
  tools_created: number;
  knowledge_bases_created: number;
  kb_documents_created: number;
  components_linked: number;
  test_scenarios_created: number;
}

export interface WorkflowMigrationResponse {
  success: boolean;
  message: string;
  target_workflow_id: number | null;
  summary: MigrationSummary | null;
}

const API_BASE = '/api';

/**
 * Turn a FastAPI error body into something a human can read.
 *
 * `detail` is not always a string. The admin-auth and super-admin routes raise
 * it as an object (`{error, message}`) so the client can branch on a stable
 * code, and FastAPI's own validation errors make it a list. The previous
 * implementation did `new Error(error.detail)` on all of them, which rendered
 * an authorization failure to the operator as the literal text
 * "[object Object]" — the one message guaranteed to say nothing at all.
 */
function errorMessage(body: any, statusCode: number): string {
  const detail = body?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => (typeof d === 'string' ? d : d?.msg))
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
    if (typeof detail.error === 'string' && detail.error.trim()) return detail.error;
  }
  if (typeof body?.message === 'string' && body.message.trim()) return body.message;
  return httpFallback(statusCode);
}

/** Plain-English fallback for when the body carried no usable message. */
function httpFallback(statusCode: number): string {
  switch (statusCode) {
    case 400: return `The request wasn't valid. Check the values and try again (HTTP 400).`;
    case 401: return `You're not signed in, or the session has expired. Sign in again (HTTP 401).`;
    case 403: return `You don't have permission for this. Check your administrator role and two-factor enrolment (HTTP 403).`;
    case 404: return `That item couldn't be found. It may have been removed, so refresh the page (HTTP 404).`;
    case 409: return `This conflicts with something that already exists. Refresh and try again (HTTP 409).`;
    case 413: return `The upload is too large for the server to accept (HTTP 413).`;
    case 422: return `Some of the values weren't accepted. Check the form and try again (HTTP 422).`;
    case 429: return `Too many requests in a short time. Wait a moment and try again (HTTP 429).`;
  }
  if (statusCode >= 500) {
    return `The server hit a problem. Try again in a minute, and check the API logs if it keeps happening (HTTP ${statusCode}).`;
  }
  return `Request failed (HTTP ${statusCode}).`;
}

/**
 * Bridge from the data API back to the auth context.
 *
 * Without this, an authorization failure is a dead end: `AdminAPI` throws, the
 * page renders the text, and the session sits there permanently unable to load
 * anything while still *looking* signed in. That is what FO-110 felt like from
 * the operator's side — the account was fine, one Cognito flag was missing, and
 * the portal had no way to say so or to recover.
 *
 * A 401/403 is information the auth layer needs, not just something to render.
 * The provider registers a handler here and decides what to do: silently renew,
 * send the admin back through enrolment, or sign them out with a real reason.
 */
export interface AdminAuthFailure {
  status: number;
  /** Stable machine code from the API (`detail.error`), when present. */
  code: string | null;
  message: string;
}

type AuthFailureHandler = (failure: AdminAuthFailure) => void;

let authFailureHandler: AuthFailureHandler | null = null;

export function setAdminAuthFailureHandler(handler: AuthFailureHandler | null): void {
  authFailureHandler = handler;
}

function errorCode(body: any): string | null {
  const detail = body?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    if (typeof detail.error === 'string') return detail.error;
  }
  return null;
}


export interface PlatformAdmin {
  email: string | null;
  status: string | null;
  enabled: boolean;
  /** The field the authorisation gate reads: false => every route 403s. */
  mfa_enrolled: boolean;
  preferred_mfa: string | null;
  created_at: string | null;
  last_modified_at: string | null;
  is_you: boolean;
}

export interface PlatformAdminDirectory {
  admins: PlatformAdmin[];
  pool_mode: string;
  group: string;
  /** Script that provisions admins; the API deliberately cannot. */
  managed_by: string;
  read_only: boolean;
}

export interface CronJob {
  job_id: string;
  name: string;
  schedule: string;
  enabled: boolean;
  notes?: string;
  last_run?: { started_at?: string; finished_at?: string; status?: string; result?: any; error?: string } | null;
}

export interface CronRun {
  id: number;
  started_at?: string;
  finished_at?: string;
  status: string;
  result?: any;
  error?: string;
}

export class AdminAPI {
  private token: string;

  constructor(token: string) {
    this.token = token;
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message = errorMessage(body, response.status);
      if (response.status === 401 || response.status === 403) {
        authFailureHandler?.({
          status: response.status,
          code: errorCode(body),
          message,
        });
      }
      throw new Error(message);
    }

    return response.json();
  }

  // Platform admin directory (FO-111). Read-only by design — the API has no
  // write access to the admin pool, so provisioning stays an operator script.
  // ── Platform crons (registry + journal + switchboard) ────────────────────
  async listCrons(): Promise<{ jobs: CronJob[] }> {
    return this.request('/super-admin/crons');
  }
  async toggleCron(jobId: string): Promise<{ jobs: CronJob[] }> {
    return this.request(`/super-admin/crons/${jobId}/toggle`, { method: 'POST' });
  }
  async runCron(jobId: string): Promise<{ status: string; result?: any; error?: string }> {
    return this.request(`/super-admin/crons/${jobId}/run`, { method: 'POST' });
  }
  async cronRuns(jobId: string): Promise<{ runs: CronRun[] }> {
    return this.request(`/super-admin/crons/${jobId}/runs`);
  }

  async getPlatformAdmins(): Promise<PlatformAdminDirectory> {
    return this.request('/admin-auth/directory');
  }

  // Trial Requests
  async getTrialRequests(statusFilter: string = 'pending'): Promise<TrialRequest[]> {
    return this.request(`/super-admin/trial-requests?status_filter=${statusFilter}`);
  }

  /** 28 Sep (mig 0702): `trialCredits` is ONE trial grant into the org's one
   *  balance; the separate navigation credits are retired (sent as 0). */
  async approveTrial(
    orgId: string,
    trialCredits: number = 100,
    maxUsers: number = 5,
    modulesToEnable: string[] = ['agents']
  ) {
    return this.request(`/super-admin/trial-requests/${orgId}/approve`, {
      method: 'POST',
      body: JSON.stringify({
        trial_credits: trialCredits,
        max_users: maxUsers,
        nav_credits: 0,
        modules_to_enable: modulesToEnable,
      }),
    });
  }

  async rejectTrial(orgId: string, reason: string) {
    return this.request(`/super-admin/trial-requests/${orgId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // Organizations
  async getOrganizations(skip: number = 0, limit: number = 100): Promise<Organization[]> {
    return this.request(`/super-admin/organizations?skip=${skip}&limit=${limit}`);
  }

  async updateTrialSettings(orgId: string, trialCredits?: number, maxUsers?: number) {
    return this.request(`/super-admin/organizations/${orgId}/trial-settings`, {
      method: 'PATCH',
      body: JSON.stringify({
        trial_credits: trialCredits,
        max_users: maxUsers,
      }),
    });
  }

  async updateOrganization(orgId: string, data: {
    name?: string;
    status?: string;
    max_users?: number;
  }) {
    return this.request(`/super-admin/organizations/${orgId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(orgId: string) {
    return this.request(`/super-admin/organizations/${orgId}`, {
      method: 'DELETE',
    });
  }

  async getOrganizationUsers(orgId: string) {
    return this.request(`/super-admin/organizations/${orgId}/users`);
  }

  // Workflow Migration
  async listAllWorkflows(organizationId?: string): Promise<WorkflowListItem[]> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    return this.request(`/super-admin/workflow-migration/workflows${params}`);
  }

  async migrateWorkflow(data: {
    source_workflow_id: number;
    source_organization_id: string;
    target_organization_id: string;
    new_workflow_name?: string;
    include_knowledge_bases?: boolean;
    include_tools?: boolean;
    include_components?: boolean;
    include_test_scenarios?: boolean;
  }): Promise<WorkflowMigrationResponse> {
    return this.request('/super-admin/workflow-migration/migrate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // User Management
  async updateUser(userId: string, data: {
    firstName?: string;
    lastName?: string;
    role?: string;
    status?: string;
  }) {
    // Convert camelCase to snake_case for API
    const apiData: any = {};
    if (data.firstName !== undefined) apiData.first_name = data.firstName;
    if (data.lastName !== undefined) apiData.last_name = data.lastName;
    if (data.role !== undefined) apiData.role = data.role;
    if (data.status !== undefined) apiData.status = data.status;
    
    return this.request(`/super-admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(apiData),
    });
  }

  async deleteUser(userId: string) {
    return this.request(`/super-admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async setUserPassword(userId: string, password: string) {
    return this.request(`/super-admin/users/${userId}/set-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async createUser(organizationId: string, data: {
    email: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    password: string;
  }) {
    // Convert camelCase to snake_case for API
    const apiData: any = {
      email: data.email,
      password: data.password,
    };
    if (data.firstName !== undefined) apiData.first_name = data.firstName;
    if (data.lastName !== undefined) apiData.last_name = data.lastName;
    if (data.role !== undefined) apiData.role = data.role;
    
    return this.request(`/super-admin/organizations/${organizationId}/users`, {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  // AWS Synchronization
  async syncFromAWS() {
    return this.request('/super-admin/sync-from-aws', {
      method: 'POST',
    });
  }

  // Signup / lead info captured at trial signup
  async getOrgLeadInfo(orgId: string): Promise<OrgLeadInfo | null> {
    return this.request(`/super-admin/organizations/${orgId}/lead-info`);
  }

  // Module Management
  async getOrgModules(orgId: string): Promise<OrgModule[]> {
    return this.request(`/super-admin/organizations/${orgId}/modules`);
  }

  async setOrgModule(orgId: string, moduleName: string, isEnabled: boolean): Promise<OrgModule> {
    return this.request(`/super-admin/organizations/${orgId}/modules`, {
      method: 'PUT',
      body: JSON.stringify({ module_name: moduleName, is_enabled: isEnabled }),
    });
  }

  // AI provider selection (thinking model / STT / TTS)
  async getOrgAISettings(orgId: string): Promise<OrgAISettingsResponse> {
    return this.request(`/super-admin/organizations/${orgId}/ai-settings`);
  }

  async setOrgAISettings(orgId: string, settings: OrgAISettings): Promise<{ settings: OrgAISettings }> {
    return this.request(`/super-admin/organizations/${orgId}/ai-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Voice evals (STT benchmark harness)
  async getEvalSpeakers(): Promise<EvalSpeaker[]> {
    return this.request('/super-admin/voice-evals/speakers');
  }

  async createEvalSpeaker(speaker: { name: string; phone_e164?: string; languages: string[]; notes?: string }): Promise<EvalSpeaker> {
    return this.request('/super-admin/voice-evals/speakers', {
      method: 'POST',
      body: JSON.stringify(speaker),
    });
  }

  async deleteEvalSpeaker(id: number): Promise<{ deleted: number }> {
    return this.request(`/super-admin/voice-evals/speakers/${id}`, { method: 'DELETE' });
  }

  async getEvalScripts(language?: string): Promise<EvalScript[]> {
    const qs = language ? `?language=${encodeURIComponent(language)}` : '';
    return this.request(`/super-admin/voice-evals/scripts${qs}`);
  }

  async getEvalRecordings(): Promise<EvalRecording[]> {
    return this.request('/super-admin/voice-evals/recordings');
  }

  async startEvalPhoneRecording(speakerId: number, scriptId: number, organizationId: string): Promise<EvalRecording> {
    return this.request('/super-admin/voice-evals/recordings/phone', {
      method: 'POST',
      body: JSON.stringify({ speaker_id: speakerId, script_id: scriptId, organization_id: organizationId }),
    });
  }

  async uploadEvalBrowserRecording(file: Blob, speakerId: number, scriptId: number): Promise<EvalRecording> {
    const form = new FormData();
    form.append('file', file, 'recording.wav');
    form.append('speaker_id', String(speakerId));
    form.append('script_id', String(scriptId));
    const response = await fetch(`${API_BASE}/super-admin/voice-evals/recordings/browser`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token}` },
      body: form,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message = errorMessage(body, response.status);
      if (response.status === 401 || response.status === 403) {
        authFailureHandler?.({ status: response.status, code: errorCode(body), message });
      }
      throw new Error(message);
    }
    return response.json();
  }

  async reprocessEvalRecording(id: number): Promise<{ processing: number }> {
    return this.request(`/super-admin/voice-evals/recordings/${id}/process`, { method: 'POST' });
  }

  async deleteEvalRecording(id: number): Promise<{ deleted: number }> {
    return this.request(`/super-admin/voice-evals/recordings/${id}`, { method: 'DELETE' });
  }

  async getEvalClips(recordingId: number): Promise<EvalClip[]> {
    return this.request(`/super-admin/voice-evals/recordings/${recordingId}/clips`);
  }

  async patchEvalClip(clipId: number, patch: { included?: boolean; ground_truth?: string; item_id?: number }): Promise<EvalClip> {
    return this.request(`/super-admin/voice-evals/clips/${clipId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  evalClipAudioUrl(clipId: number): string {
    return `${API_BASE}/super-admin/voice-evals/clips/${clipId}/audio`;
  }

  async fetchEvalAudio(path: string): Promise<Blob> {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!response.ok) throw new Error(`Couldn't load this clip's audio (HTTP ${response.status}).`);
    return response.blob();
  }

  async getEvalRuns(): Promise<EvalRun[]> {
    return this.request('/super-admin/voice-evals/runs');
  }

  async createEvalRun(run: { name?: string; providers: string[]; language?: string; source?: string; category?: string }): Promise<EvalRun> {
    return this.request('/super-admin/voice-evals/runs', {
      method: 'POST',
      body: JSON.stringify(run),
    });
  }

  async getEvalRunDetails(runId: number): Promise<EvalRunDetails> {
    return this.request(`/super-admin/voice-evals/runs/${runId}`);
  }

  // Token validation
  async verifyToken(): Promise<boolean> {
    try {
      await this.request('/super-admin/health');
      return true;
    } catch {
      return false;
    }
  }

  // Organization Management
  async createOrganization(data: {
    organizationName: string;
    organizationSlug: string;
    maxUsers?: number;
    /** 28 Sep (mig 0702): a starting grant (credit lot, source admin_grant). */
    startingCredits?: number;
    ownerEmail: string;
    ownerFirstName?: string;
    ownerLastName?: string;
    ownerPassword: string;
  }) {
    // Convert camelCase to snake_case for API
    const apiData: any = {
      organization_name: data.organizationName,
      organization_slug: data.organizationSlug,
      owner_email: data.ownerEmail,
      owner_password: data.ownerPassword,
    };
    if (data.maxUsers !== undefined) apiData.max_users = data.maxUsers;
    // Sent under both names while the API moves over: `conversation_credits`
    // is the field it reads today, and it now lands as a grant, not a counter.
    if (data.startingCredits !== undefined) {
      apiData.starting_credits = data.startingCredits;
      apiData.conversation_credits = data.startingCredits;
    }
    if (data.ownerFirstName !== undefined) apiData.owner_first_name = data.ownerFirstName;
    if (data.ownerLastName !== undefined) apiData.owner_last_name = data.ownerLastName;
    
    return this.request('/super-admin/organizations', {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  // ----- Billing / cost / activity -----

  // ----- The rate card (28 Sep, mig 0702) -----
  //
  // ONE balance per organisation, ONE rate card: credit_pricing_defaults is the
  // global card, and an organisation can have its own number for any row
  // (credit_pricing_overrides, agent_id NULL). Agent-level prices, money rates,
  // plans and packs are retired — their write routes answer 410.

  /** The global card: every action with its plain label, unit and family,
   *  including the ones never charged (customer_visible=false). */
  async getCreditDefaults(): Promise<CreditDefault[]> {
    return this.request('/super-admin/billing/pricing/defaults');
  }

  async updateCreditDefault(action: string, credits: number): Promise<CreditDefault> {
    return this.request(`/super-admin/billing/pricing/defaults/${action}`, {
      method: 'PUT',
      body: JSON.stringify({ credits }),
    });
  }

  /** Every organisation's own prices (org rows only). */
  async getCreditOverrides(orgId?: string): Promise<CreditOverride[]> {
    const qs = orgId ? `?organization_id=${orgId}` : '';
    return this.request(`/super-admin/billing/pricing/overrides${qs}`);
  }

  /** One organisation's effective card: each row says Default or agreed for this org. */
  async getEffectiveRateCard(orgId: string): Promise<RateCardRow[]> {
    const r: { rate_card?: RateCardRow[] } | RateCardRow[] = await this.request(
      `/super-admin/billing/orgs/${orgId}/rate-card`,
    );
    return Array.isArray(r) ? r : r.rate_card ?? [];
  }

  /** Set this organisation's own price for one action. */
  async setOrgOverride(orgId: string, action: string, credits: number): Promise<RateCardRow[]> {
    const r: { rate_card?: RateCardRow[] } = await this.request(
      `/super-admin/billing/orgs/${orgId}/rate-card/${encodeURIComponent(action)}`,
      { method: 'PUT', body: JSON.stringify({ credits }) },
    );
    return r.rate_card ?? [];
  }

  /** Clear it — the organisation goes back to the default for that action. */
  async clearOrgOverride(orgId: string, action: string): Promise<RateCardRow[]> {
    const r: { rate_card?: RateCardRow[] } = await this.request(
      `/super-admin/billing/orgs/${orgId}/rate-card/${encodeURIComponent(action)}`,
      { method: 'PUT', body: JSON.stringify({ credits: null }) },
    );
    return r.rate_card ?? [];
  }

  // Retired plans stay readable (history only) so old payment rows explain themselves.
  async getSubscriptionTiers(): Promise<SubscriptionTier[]> {
    return this.request('/super-admin/billing/subscription-tiers');
  }

  async getBlockedDomains(): Promise<BlockedDomain[]> {
    return this.request('/super-admin/billing/blocked-domains');
  }

  async addBlockedDomain(domain: string, reason?: string): Promise<BlockedDomain> {
    return this.request('/super-admin/billing/blocked-domains', {
      method: 'POST',
      body: JSON.stringify({ domain, reason }),
    });
  }

  async removeBlockedDomain(domain: string): Promise<{ domain: string; is_active: boolean }> {
    return this.request(`/super-admin/billing/blocked-domains/${encodeURIComponent(domain)}`, {
      method: 'DELETE',
    });
  }

  async getLLMPricing(): Promise<LLMPricing[]> {
    return this.request('/super-admin/billing/llm-pricing');
  }

  async updateLLMPricing(id: number, body: UpdateLLMPricingBody): Promise<LLMPricing> {
    return this.request(`/super-admin/billing/llm-pricing/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async getUsageSummary(params: { organization_id?: string; from?: string; to?: string }): Promise<UsageSummary> {
    const qs = new URLSearchParams();
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return this.request(`/super-admin/billing/usage/summary?${qs}`);
  }

  async getUsageTimeseries(params: { organization_id?: string; from?: string; to?: string }): Promise<UsageTimeseries> {
    const qs = new URLSearchParams();
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return this.request(`/super-admin/billing/usage/timeseries?${qs}`);
  }

  // ----- Usage & Cost v2 (live ledger; includes today) -----

  private usageQs(params: { organization_id?: string; agent?: string; from?: string; to?: string }): URLSearchParams {
    const qs = new URLSearchParams();
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.agent) qs.set('agent', params.agent);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return qs;
  }

  async getUsageOverview(params: { organization_id?: string; agent?: string; from?: string; to?: string }): Promise<UsageOverview> {
    return this.request(`/super-admin/billing/usage/overview?${this.usageQs(params)}`);
  }

  async getUsageDaily(params: { organization_id?: string; agent?: string; from?: string; to?: string }): Promise<UsageDaily> {
    return this.request(`/super-admin/billing/usage/daily?${this.usageQs(params)}`);
  }

  async getUsageAgents(params: { organization_id?: string; from?: string; to?: string; q?: string; limit?: number }): Promise<UsageAgents> {
    const qs = this.usageQs(params);
    if (params.q) qs.set('q', params.q);
    if (params.limit != null) qs.set('limit', String(params.limit));
    return this.request(`/super-admin/billing/usage/agents?${qs}`);
  }

  async getUsageByEntity(params: { organization_id?: string; entity_type: string; from?: string; to?: string }): Promise<UsageByEntity> {
    const qs = new URLSearchParams({ entity_type: params.entity_type });
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return this.request(`/super-admin/billing/usage/by-entity?${qs}`);
  }

  async getActivityTimeseries(params: { organization_id?: string; type?: string; from?: string; to?: string }): Promise<ActivityTimeseries> {
    const qs = new URLSearchParams();
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.type) qs.set('type', params.type);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return this.request(`/super-admin/billing/activity/timeseries?${qs}`);
  }

  async runRollup(day?: string): Promise<{ day: string; rows: number }> {
    const qs = day ? `?day=${day}` : '';
    return this.request(`/super-admin/billing/rollup/run${qs}`, { method: 'POST' });
  }

  async getLineItems(params: {
    agent_kind?: AgentKind;
    interaction_type?: InteractionType;
    organization_id?: string;
    agent?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<LineItemsResponse> {
    const qs = new URLSearchParams();
    if (params.agent_kind) qs.set('agent_kind', params.agent_kind);
    if (params.interaction_type) qs.set('interaction_type', params.interaction_type);
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.agent) qs.set('agent', params.agent);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    return this.request(`/super-admin/billing/line-items?${qs}`);
  }

  async getLineItemCalls(logicalUnitKey: string): Promise<LineItemCallsResponse> {
    return this.request(
      `/super-admin/billing/line-items/${encodeURIComponent(logicalUnitKey)}/calls`,
    );
  }

  /**
   * Add credits to an organisation's one balance (28 Sep, mig 0702): one lot,
   * org-wide, lasting `validityDays` (a year by default). There is no per-agent
   * grant any more — every agent draws from the organisation's credits.
   */
  async grantCredits(
    organizationId: string,
    credits: number,
    note?: string,
    validityDays = 365,
  ): Promise<{ id: string; credits_left?: number; total_credits_balance?: number }> {
    return this.request(`/super-admin/billing/orgs/${organizationId}/grant-credits`, {
      method: 'POST',
      body: JSON.stringify({ credits, note, validity_days: validityDays }),
    });
  }

  /** Grant for a lead batch: leads × credits per lead, typed for THIS batch
   *  (there is no stored default), as one expiring lot. */
  async grantForLeadBatch(
    organizationId: string,
    body: { leads_count: number; credits_per_lead: number; note?: string; contact_list_id?: string | null; validity_days?: number },
  ): Promise<{ batch_id: number; lot_id: number; leads_count: number; credits_per_lead: number; credits_granted: number }> {
    return this.request(`/super-admin/billing/orgs/${organizationId}/preload-leads`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** What was agreed with an organisation, in credits. per_lead and
   *  success_fees ARE its rate-card prices (lead_loaded / outcome_*). */
  async getOrgTerms(organizationId: string): Promise<OrgTerms> {
    return this.request(`/super-admin/billing/orgs/${organizationId}/terms`);
  }

  /** A success fee of null clears that override back to the default. */
  async setOrgTerms(
    organizationId: string,
    terms: { use_case?: string | null; notes?: string | null; per_lead?: number | null; success_fees?: Record<string, number | null> },
  ): Promise<OrgTerms> {
    return this.request(`/super-admin/billing/orgs/${organizationId}/terms`, {
      method: 'PUT',
      body: JSON.stringify(terms),
    });
  }

  /** The organisation's credits read model (the same one its Credits page reads). */
  async getOrgCreditsOverview(organizationId: string): Promise<OrgCreditsOverview> {
    return this.request(`/super-admin/billing/orgs/${organizationId}/credits-overview`);
  }

  /** The organisation's latest ledger rows (spends, grants, the 0702 merge row). */
  async getOrgLedger(organizationId: string, limit = 20): Promise<OrgLedgerRow[]> {
    const r: { events?: OrgLedgerRow[] } | OrgLedgerRow[] = await this.request(
      `/super-admin/billing/orgs/${organizationId}/ledger?limit=${limit}`,
    );
    return Array.isArray(r) ? r : r.events ?? [];
  }

  // ----- Usage & cost reporting (mig 0585) -------------------------------
  //
  // One filter shape for every endpoint, so a number on the page and the CSV
  // beside it are always the same query. `usageParams` is the only place that
  // knows how a filter becomes a query string.

  private usageParams(f: UsageFilterState, extra?: Record<string, string | number | boolean | undefined>) {
    const qs = new URLSearchParams();
    const put = (k: string, v: unknown) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    };
    put('from', f.from);
    put('to', f.to);
    put('organization_id', f.organizationId);
    put('agent', f.agent);
    put('channel', f.channel);
    put('modality', f.modality);
    put('agent_kind', f.agentKind);
    put('provider', f.provider);
    put('feature', f.feature);
    put('campaign', f.campaign);
    put('customer', f.customer);
    put('include_simulated', f.includeSimulated ? 'true' : undefined);
    for (const [k, v] of Object.entries(extra || {})) put(k, v);
    return qs;
  }

  async getUsageFilterOptions(params: { q?: string; organization_id?: string } = {}): Promise<UsageFilterOptions> {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    return this.request(`/super-admin/usage/filters?${qs}`);
  }

  async getUsageReportSummary(f: UsageFilterState): Promise<UsageReportSummary> {
    return this.request(`/super-admin/usage/summary?${this.usageParams(f)}`);
  }

  async getUsageReportTimeseries(f: UsageFilterState, split: UsageSplit): Promise<UsageReportTimeseries> {
    return this.request(`/super-admin/usage/timeseries?${this.usageParams(f, { split })}`);
  }

  async getUsageBreakdown(f: UsageFilterState, dimension: UsageDimension, limit = 50): Promise<UsageBreakdown> {
    return this.request(`/super-admin/usage/breakdown?${this.usageParams(f, { dimension, limit })}`);
  }

  async getUsageSessions(
    f: UsageFilterState,
    opts: { sort?: UsageSessionSort; limit?: number; offset?: number; include_non_session?: boolean } = {},
  ): Promise<UsageSessionsResponse> {
    return this.request(`/super-admin/usage/sessions?${this.usageParams(f, {
      sort: opts.sort, limit: opts.limit, offset: opts.offset,
      include_non_session: opts.include_non_session,
    })}`);
  }

  async getUsageSessionEvents(sessionKey: string): Promise<UsageSessionEvents> {
    return this.request(`/super-admin/usage/sessions/${encodeURIComponent(sessionKey)}/events`);
  }

  async getUsageRateCard(): Promise<UsageRateCard> {
    return this.request('/super-admin/usage/rate-card');
  }

  async upsertCarrierRate(body: CarrierRateUpsert): Promise<CarrierRate> {
    return this.request('/super-admin/usage/rate-card/telephony', {
      method: 'POST', body: JSON.stringify(body),
    });
  }

  async upsertMessagingRate(body: MessagingRateUpsert): Promise<{ status: string }> {
    return this.request('/super-admin/usage/rate-card/messaging', {
      method: 'POST', body: JSON.stringify(body),
    });
  }

  /**
   * CSV of the current filter selection.
   *
   * Goes through fetch rather than `request` because the response is a file,
   * not JSON — and it carries the bearer token, so it cannot be a plain link.
   */
  async downloadUsageCsv(f: UsageFilterState): Promise<Blob> {
    const response = await fetch(`${API_BASE}/super-admin/usage/export.csv?${this.usageParams(f)}`, {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!response.ok) throw new Error(`Couldn't export the CSV. Narrow the date range and try again (HTTP ${response.status}).`);
    return response.blob();
  }
}

// ----- Usage & cost reporting types ------------------------------------

export type UsageChannel = 'ui' | 'phone' | 'messaging' | 'api' | 'system' | 'unknown';
export type UsageModality = 'llm' | 'tts' | 'stt' | 'telephony' | 'messaging';
export type UsageAgentKind = 'ui' | 'phone' | 'messaging' | 'nav';
export type UsageSplit = 'modality' | 'channel' | 'agent_kind' | 'none';
export type UsageDimension =
  | 'channel' | 'modality' | 'agent_kind' | 'provider' | 'model'
  | 'feature' | 'agent' | 'org' | 'status' | 'campaign' | 'customer' | 'transport';
export type UsageSessionSort = 'cost' | 'cost_per_minute' | 'duration' | 'started' | 'calls';
/** How a session's duration was measured. null = it wasn't. */
export type DurationSource = 'carrier' | 'engaged' | null;

export interface UsageFilterState {
  from: string;
  to: string;
  organizationId?: string;
  agent?: string;
  channel?: UsageChannel;
  modality?: UsageModality;
  agentKind?: UsageAgentKind;
  provider?: string;
  feature?: string;
  campaign?: string;
  customer?: string;
  /** Simulator runs ('SIM-' call ids) are excluded from every figure unless set. */
  includeSimulated?: boolean;
}

export interface UsageFilterOptions {
  organizations: { id: string; name: string }[];
  agents: {
    agent_ref: string; name: string; organization_id: string | null;
    agent_channel: string; channels: string[];
  }[];
  campaigns: {
    id: string; name: string; organization_id: string | null;
    channel: string; status: string;
  }[];
  /** Spend-first, not alphabetical: the book can run to six figures. */
  customers: {
    id: string; name: string | null; phone: string | null;
    organization_id: string | null; cost_usd: number;
  }[];
  channels: UsageChannel[];
  modalities: UsageModality[];
  agent_kinds: UsageAgentKind[];
  providers: string[];
  features: string[];
  dimensions: UsageDimension[];
}

export interface UsageChannelRow {
  channel: UsageChannel;
  sessions: number;
  non_session_units: number;
  cost_usd: number;
  session_cost_usd: number;
  non_session_cost_usd: number;
  llm_cost: number;
  tts_cost: number;
  stt_cost: number;
  telephony_cost: number;
  minutes: number;
  metered_minutes: number;
  metered_sessions: number;
  credits_billable: number;
  unpriced_sessions: number;
  cost_per_session: number | null;
  cost_per_minute: number | null;
  cost_inr: number | null;
  session_cost_inr: number | null;
  cost_per_session_inr: number | null;
  cost_per_minute_inr: number | null;
  /** What this channel sells, and how many of them: call | session | message. */
  unit_label: 'call' | 'session' | 'message';
  units: number;
  cost_per_unit: number | null;
  cost_per_unit_inr: number | null;
  /** Minutes behind cost_per_minute: carrier talk time on phone, engaged time on screen. */
  rated_minutes: number;
  rated_cost_usd: number;
  llm_per_minute: number | null;
  tts_per_minute: number | null;
  stt_per_minute: number | null;
  telephony_per_minute: number | null;
  avg_minutes_per_unit: number | null;
  turns: number;
  turns_per_unit: number | null;
  cost_per_turn: number | null;
  tokens_per_turn: number | null;
  tts_chars_per_minute: number | null;
  connected_calls: number;
  handoffs: number;
  handoff_rate: number | null;
  messages: number;
  overhead_usd: number;
  overhead_inr: number | null;
  overhead_per_unit: number | null;
  overhead_per_unit_inr: number | null;
  /** Conversation cost plus the analysis it caused — per unit, never per minute. */
  allin_cost_usd: number;
  allin_cost_inr: number | null;
  allin_per_unit: number | null;
  allin_per_unit_inr: number | null;
  llm_per_turn: number | null;
  tts_per_turn: number | null;
  stt_requests: number;
  stt_seconds: number;
  stt_per_request: number | null;
  stt_seconds_per_request: number | null;
  revenue_per_unit: number | null;
  margin_per_unit: number | null;
  margin_allin_usd: number;
  margin_allin_pct: number | null;
  voice_share_pct: number | null;
  revenue_usd: number;
  revenue_per_session: number | null;
  margin_usd: number;
  margin_pct: number | null;
  duration_basis: 'metered' | 'engagement';
}

export interface UsageReportSummary {
  filters: Record<string, string | null>;
  totals: {
    calls: number; cost_usd: number; input_tokens: number; output_tokens: number;
    chars: number; audio_seconds: number; errors: number; orgs: number; agents: number;
    sessions: number; minutes: number;
    session_cost_usd: number; non_session_cost_usd: number;
    revenue_usd: number; margin_usd: number; margin_pct: number | null;
    cost_per_session: number | null; cost_per_minute: number | null;
    cost_inr: number | null; session_cost_inr: number | null;
    cost_per_session_inr: number | null; cost_per_minute_inr: number | null;
    allin_session_cost_usd: number; allin_session_cost_inr: number | null;
    cost_per_session_allin: number | null; cost_per_session_allin_inr: number | null;
    margin_allin_usd: number; margin_allin_pct: number | null;
  };
  /** The pulses a true per-call cost must model (mig 0594). */
  pulses: {
    carrier: {
      provider: string; direction: string; country_code: string; per_minute: number; per_call: number;
      billing_increment_seconds: number; minimum_seconds: number; currency: string;
      per_minute_usd: number | null; per_call_usd: number | null; fx: string;
    } | null;
    stt: {
      provider: string; model: string; per_minute_usd: number | null;
      billing_increment_seconds: number; minimum_seconds: number; requests_in_window: number; notes?: string | null;
    } | null;
  };
  back_office: {
    total_usd: number; attributed_usd: number; unattributed_usd: number;
    by_feature: { feature: string; cost_usd: number; calls: number }[];
  };
  /** The newest fixing on file — what the ₹ figures are anchored to. */
  fx: { base: string; quote: string; rate: number; as_of: string; source: string } | null;
  /** What the default view left out (null when simulator runs are included). */
  simulated: { sessions: number; cost_usd: number } | null;
  /** Every call attempt in the window from the call log, simulator excluded. */
  phone_funnel: {
    placed: number; connected: number; inbound: number; outbound: number; handoffs: number;
    talk_seconds: number; billed_seconds: number; unconnected_cost_usd: number;
  } | null;
  previous: { cost_usd: number; calls: number; cost_delta_pct: number | null };
  by_modality: {
    modality: UsageModality; calls: number; cost_usd: number;
    input_tokens: number; output_tokens: number; chars: number; audio_seconds: number;
  }[];
  by_channel: UsageChannelRow[];
  price_per_credit: number;
  unattributed: { cost_usd: number; calls: number } | null;
  unpriced_models: {
    provider: string; model: string; modality: UsageModality;
    calls: number; tokens: number; chars: number; audio_seconds: number;
  }[];
  uncosted_calls: { calls: number; seconds: number };
}

export interface UsageReportTimeseries {
  split: UsageSplit;
  series_keys: string[];
  points: { day: string; calls: number; cost_usd: number; series: Record<string, number> }[];
}

export interface UsageBreakdownRow {
  key: string | null;
  label: string;
  calls: number;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  chars: number;
  audio_seconds: number;
  errors: number;
  sessions: number;
  sample_modality: UsageModality;
  share_pct: number;
  cost_per_session: number | null;
  cost_inr: number | null;
  priced?: boolean;
  rate?: {
    provider: string; model: string;
    input_per_mtok: number; output_per_mtok: number;
    per_million_chars: number | null; per_audio_minute: number | null;
  } | null;
}

export interface UsageBreakdown {
  dimension: UsageDimension;
  rows: UsageBreakdownRow[];
}

export interface UsageSessionRow {
  session_key: string;
  organization_id: string | null;
  organization_name: string;
  agent_ref: string | null;
  agent_name: string;
  channel: UsageChannel;
  /** whatsapp | sms | rcs — which wire a messaging session left on. */
  transport: string | null;
  agent_kind: UsageAgentKind;
  started_at: string;
  last_event_at: string;
  duration_seconds: number | null;
  duration_source: DurationSource;
  duration_is_metered: boolean;
  is_session: boolean;
  /** A simulator run: real pipeline, no customer, no carrier minutes. */
  simulated: boolean;
  direction: string | null;
  disposition: string | null;
  calls: number;
  llm_calls: number;
  tts_calls: number;
  stt_calls: number;
  input_tokens: number;
  output_tokens: number;
  chars: number;
  audio_seconds: number;
  cost_usd: number;
  llm_cost: number;
  tts_cost: number;
  stt_cost: number;
  telephony_cost: number;
  active_ms: number;
  any_error: boolean;
  minutes: number | null;
  cost_per_minute: number | null;
  /** Rupees at each day's mid-market fixing; null when no rate exists. */
  cost_inr: number | null;
  cost_per_minute_inr: number | null;
  llm_per_minute: number | null;
  tts_per_minute: number | null;
  stt_per_minute: number | null;
  telephony_per_minute: number | null;
  turns: number;
  cost_per_turn: number | null;
  tokens_per_turn: number | null;
  /** Analysis run on this thread afterwards, charged to its first leg. */
  overhead_usd: number | null;
  overhead_inr: number | null;
  credits_billable: number | null;
  revenue_usd: number;
  margin_usd: number;
  per_session_credits: number | null;
  per_minute_credits: number | null;
  included_minutes: number | null;
  minimum_minutes: number | null;
  rounding_seconds: number | null;
}

export interface UsageSessionsResponse {
  sort: UsageSessionSort;
  total: number;
  limit: number;
  offset: number;
  rows: UsageSessionRow[];
}

export interface UsageSessionEvents {
  session_key: string;
  call: {
    id: number; direction: string; provider: string; status: string;
    disposition: string | null; hangup_reason: string | null;
    started_at: string; ended_at: string | null;
    duration_seconds: number | null; billed_seconds: number | null;
    rate_per_minute: number | null; cost_usd: number | null; currency: string | null;
  } | null;
  events: {
    id: number; created_at: string; provider: string; model: string;
    modality: UsageModality; feature: string; status: string;
    input_tokens: number; output_tokens: number;
    char_count: number | null; audio_seconds: number | null;
    cost_usd: number; duration_ms: number | null;
    baml_function: string | null; metadata: Record<string, unknown>;
  }[];
  totals: { calls: number; cost_usd: number };
}

export interface CarrierRate {
  id: number;
  provider: 'exotel' | 'twilio';
  direction: 'inbound' | 'outbound' | 'any';
  country_code: string;
  per_minute: number;
  per_call: number;
  billing_increment_seconds: number;
  minimum_seconds: number;
  currency: string;
  effective_from: string;
  notes: string | null;
}

export type CarrierRateUpsert = Omit<CarrierRate, 'id' | 'effective_from'>;

export interface ChannelRateCard {
  id: number;
  organization_id: string | null;
  organization_name?: string | null;
  agent_ref: string | null;
  channel: 'ui' | 'phone' | 'messaging';
  per_session_credits: number;
  per_minute_credits: number;
  included_minutes: number;
  minimum_minutes: number;
  rounding_seconds: number;
  is_active: boolean;
  notes: string | null;
  updated_at?: string;
  updated_by?: string | null;
  scope: 'global' | 'organization' | 'agent';
}

export interface MessagingRate {
  id: number;
  provider: string;
  channel: 'whatsapp' | 'sms' | 'rcs';
  category: 'marketing' | 'utility' | 'authentication' | 'service' | 'any';
  direction: string;
  country_code: string;
  per_message: number;
  per_conversation: number;
  /** 0 = charged per message regardless of length; 160 = per SMS segment. */
  segment_chars: number;
  currency: string;
  effective_from: string;
  notes: string | null;
}

export interface MessagingRateUpsert {
  provider: string;
  channel: string;
  category?: string;
  direction?: string;
  country_code?: string;
  per_message?: number;
  per_conversation?: number;
  segment_chars?: number;
  currency?: string;
  notes?: string | null;
}

/** A send that matched no rate row — the gap a rate card row would close. */
export interface UnpricedMessaging {
  provider: string | null;
  channel: string | null;
  category: string | null;
  country_code: string | null;
  messages: number;
  last_seen: string;
}

export interface UsageRateCard {
  models: LLMPricing[];
  carriers: CarrierRate[];
  messaging: MessagingRate[];
  unpriced_messaging: UnpricedMessaging[];
  channel_rate_cards: ChannelRateCard[];
  credit_actions: { action: string; credits: number; updated_at: string | null }[];
  pricing_settings: { currency: string; base_price_per_credit: number; annual_discount_pct: number }[];
}

// ----- Billing types (28 Sep, mig 0702: one balance, one rate card) -----

export interface CreditDefault {
  action: string;
  credits: number;
  updated_at?: string | null;
  updated_by?: string | null;
  label?: string | null;
  unit?: string | null;
  family?: string | null;
  /** false = never charged (retired or unused) — shown as "not charged". */
  customer_visible?: boolean;
}

export interface CreditOverride {
  id: number;
  organization_id: string;
  organization_name?: string | null;
  action: string;
  credits: number;
  updated_at?: string | null;
}

export interface RateCardRow {
  action: string;
  credits: number;
  default_credits?: number | null;
  /** 'org_override' = agreed for this organisation; 'default' = the global card. */
  resolved_from: 'org_override' | 'default' | string;
  label: string;
  unit: string;
  family: string;
  customer_visible?: boolean;
}

export interface OrgTerms {
  use_case: string | null;
  notes: string | null;
  per_lead: number | null;
  /** Keyed qualified / meeting_booked / converted. */
  success_fees: Record<string, number>;
  per_lead_agreed?: boolean;
  success_fees_agreed?: Record<string, boolean>;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface OrgCreditLot {
  id: number;
  source: string;
  granted: number;
  remaining: number;
  granted_at: string;
  expires_at: string | null;
  status: string;
  metadata?: Record<string, unknown> | null;
}

export interface OrgCreditsOverview {
  organization_name?: string | null;
  billing_state?: string | null;
  balance: number;
  credits_left?: number;
  runway_days?: number | null;
  expiring_30d?: { credits: number; first_expiry: string | null } | number;
  trial_expires_at?: string | null;
  trial_days_left?: number | null;
  burn: { last_7d: number; last_30d: number; granted_30d: number; per_day: number; runway_days: number | null };
  lots: OrgCreditLot[];
  terms: OrgTerms;
  rate_card: RateCardRow[];
}

export interface OrgLedgerRow {
  id: number;
  action: string;
  credits: number;
  label?: string;
  entity_type?: string | null;
  metadata?: Record<string, any> | null;
  created_at: string;
  agent_ref?: string | null;
}

/** Retired 28 Sep: kept read-only so old payment rows stay explainable. */
export interface SubscriptionTier {
  id: number;
  code: string;
  display_name: string;
  region: 'india' | 'global';
  currency: string;
  amount_minor: number;
  credits: number;
  discount_pct: number;
  bonus_pct: number;
  validity_days: number;
  provider: string;
  provider_plan_id: string | null;
  is_active: boolean;
  is_custom: boolean;
  sort_order: number;
}

export interface BlockedDomain {
  domain: string;
  reason: string | null;
  added_by: string | null;
  is_active: boolean;
  created_at: string;
}

export interface LLMPricing {
  id: number;
  provider: string;
  model: string;
  modality: Modality;
  input_per_mtok: number;
  output_per_mtok: number;
  per_million_chars?: number | null;
  per_audio_minute?: number | null;
  effective_from: string;
  effective_to?: string | null;
  notes?: string | null;
}

export interface UpdateLLMPricingBody {
  input_per_mtok?: number;
  output_per_mtok?: number;
  per_million_chars?: number | null;
  per_audio_minute?: number | null;
  notes?: string | null;
}

export interface UsageFeatureRow {
  feature: string;
  llm_calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  credits_spent: number;
}

export interface UsageSummary {
  from: string;
  to: string;
  organization_id: string | null;
  totals: {
    llm_calls: number;
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
    credits_spent: number;
  };
  by_feature: UsageFeatureRow[];
  by_modality?: { modality: Modality; calls: number; cost_usd: number }[];
}

export interface UsageTimeseries {
  from: string;
  to: string;
  points: { day: string; feature: string; cost_usd: number; credits_spent: number; llm_calls: number }[];
}

// ----- Usage & Cost v2 -----

export interface UsageModalityRow {
  modality: Modality;
  calls: number;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  chars: number;
  audio_seconds: number;
}

export interface UsageModelRow {
  provider: string;
  model: string;
  modality: Modality;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  chars: number;
  audio_seconds: number;
  cost_usd: number;
  errors: number;
  rate_input_per_mtok: number | null;
  rate_output_per_mtok: number | null;
  rate_per_million_chars: number | null;
  rate_per_audio_minute: number | null;
  priced: boolean;
}

export interface UsageOverview {
  from: string;
  to: string;
  organization_id: string | null;
  agent: string | null;
  totals: {
    cost_usd: number;
    calls: number;
    input_tokens: number;
    output_tokens: number;
    credits_spent: number;
  };
  by_modality: UsageModalityRow[];
  by_feature: { feature: string; calls: number; cost_usd: number }[];
  by_model: UsageModelRow[];
  by_org: { organization_id: string; organization_name: string | null; calls: number; cost_usd: number }[];
  unattributed: { cost_usd: number; calls: number } | null;
}

export interface UsageDailyPoint {
  day: string;
  llm_cost: number;
  tts_cost: number;
  stt_cost: number;
  total_cost: number;
  calls: number;
  credits: number;
}

export interface UsageDaily {
  from: string;
  to: string;
  points: UsageDailyPoint[];
}

export interface UsageAgentRow {
  agent_key: string;
  kind: 'app' | 'nav';
  name: string;
  organization_id: string | null;
  calls: number;
  cost_usd: number;
  llm_cost: number;
  tts_cost: number;
  stt_cost: number;
  total_tokens: number;
}

export interface UsageAgents {
  from: string;
  to: string;
  organization_id: string | null;
  rows: UsageAgentRow[];
  unresolved: { calls: number; cost_usd: number } | null;
}

export interface UsageByEntity {
  from: string;
  to: string;
  entity_type: string;
  rows: { entity_id: string; entity_name?: string; llm_calls: number; cost_usd: number; total_tokens: number }[];
}

export interface ActivityTimeseries {
  from: string;
  to: string;
  points: { day: string; type: string; count: number; unique_users: number }[];
  tiles: { type: string; count: number; unique_users: number }[];
}

// ----- Per-line-item cost types -----

export type AgentKind = 'ui' | 'nav';

export type InteractionType =
  | 'ui_interaction'
  | 'ui_agent_creation'
  | 'ui_component_creation'
  | 'nav_session'
  | 'nav_configuration';

export interface LineItem {
  logical_unit_key: string;
  agent_kind: AgentKind;
  interaction_type: InteractionType;
  organization_id: string;
  organization_name?: string | null;
  entity: { type: string; id: string; label: string };
  started_at: string | null;
  ended_at: string | null;
  /** Wall-clock span of the unit (last event − first event), seconds. */
  duration_seconds: number;
  /** Time actually spent inside LLM/TTS/STT calls (sum of latencies), ms. */
  active_ms: number;
  llm_call_count: number;
  tts_call_count: number;
  stt_call_count: number;
  input_tokens: number;
  output_tokens: number;
  char_count: number;
  audio_seconds: number;
  // Total cost plus the LLM / TTS / STT split.
  cost_usd: number;
  llm_cost_usd: number;
  tts_cost_usd: number;
  stt_cost_usd: number;
  credits: number;
  status: 'success' | 'error';
  flags: { reanalysis: boolean };
  extra: Record<string, unknown>;
}

export interface LineItemsResponse {
  from: string;
  to: string;
  agent_kind: AgentKind | null;
  interaction_type: InteractionType | null;
  organization_id: string | null;
  limit: number;
  offset: number;
  rows: LineItem[];
  has_more: boolean;
}

export type Modality = 'llm' | 'tts' | 'stt';

export interface LineItemCall {
  id: number;
  created_at: string | null;
  modality: Modality;
  provider: string;
  model: string;
  baml_function: string | null;
  input_tokens: number;
  output_tokens: number;
  char_count: number | null;
  audio_seconds: number | null;
  cost_usd: number;
  duration_ms: number | null;
  status: string;
  metadata: Record<string, unknown>;
}

export interface LineItemCallsResponse {
  logical_unit_key: string;
  interaction_type: InteractionType;
  calls: LineItemCall[];
}

// ── AI provider selection ────────────────────────────────────────────────────

export interface OrgAISettings {
  llm: { client: string };
  stt: { provider: string; model: string | null };
  tts: { provider: string };
}

export interface AIProviderOption {
  value: string;
  label: string;
  available: boolean;
}

export interface OrgAISettingsResponse {
  settings: OrgAISettings;
  options: { llm: AIProviderOption[]; stt: AIProviderOption[]; tts: AIProviderOption[] };
}

// ── Voice evals ──────────────────────────────────────────────────────────────

export interface EvalSpeaker {
  id: number;
  name: string;
  phone_e164: string | null;
  languages: string[];
  notes: string | null;
}

export interface EvalScriptItem {
  id: number;
  idx: number;
  category: 'sentence' | 'short_phrase' | 'one_word';
  text: string;
}

export interface EvalScript {
  id: number;
  language: string;
  name: string;
  items: EvalScriptItem[];
}

export interface EvalRecording {
  id: number;
  speaker_id: number | null;
  script_id: number | null;
  speaker_name?: string | null;
  script_name?: string | null;
  language: string;
  source: 'phone' | 'browser';
  status: 'pending' | 'dialing' | 'recording' | 'processing' | 'ready' | 'failed';
  call_sid: string | null;
  duration_ms: number | null;
  clip_count?: number;
  error: string | null;
  created_at: string;
}

export interface EvalClip {
  id: number;
  recording_id: number;
  item_id: number | null;
  item_idx?: number | null;
  item_text?: string | null;
  category: 'sentence' | 'short_phrase' | 'one_word' | 'silence';
  language: string;
  source: string;
  ground_truth: string;
  duration_ms: number | null;
  align_sim: number | null;
  align_transcript: string | null;
  included: boolean;
}

export interface EvalRun {
  id: number;
  name: string | null;
  providers: string[];
  filters: Record<string, string>;
  status: 'running' | 'done' | 'failed';
  error?: string | null;
  result_count?: number;
  created_at: string;
  finished_at: string | null;
}

export interface EvalAggregate {
  provider: string;
  category: string;
  language: string;
  source: string;
  clips: number;
  avg_wer: number | null;
  avg_sim: number | null;
  avg_latency_ms: number | null;
  errors: number;
}

export interface EvalResultRow {
  id: number;
  clip_id: number;
  provider: string;
  transcript: string | null;
  wer: number | null;
  sim: number | null;
  latency_ms: number | null;
  error: string | null;
  category: string;
  language: string;
  source: string;
  ground_truth: string;
  duration_ms: number | null;
}

export interface EvalRunDetails {
  run: EvalRun;
  aggregates: EvalAggregate[];
  results: EvalResultRow[];
}

